import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Search, LayoutGrid, User } from "lucide-react";
import { motion } from "framer-motion";

export const AppFrame = ({ children }) => (
  <div
    className="mx-auto max-w-md relative min-h-screen pb-24 overflow-hidden grain"
    style={{ background: "var(--bg)", boxShadow: "0 40px 80px -20px rgba(27,25,23,0.15)" }}
  >
    <div className="relative z-10">{children}</div>
  </div>
);

export const TopBar = ({ title, subtitle, onBack, right }) => {
  const navigate = useNavigate();
  return (
    <div
      className="sticky top-0 z-40 backdrop-blur-xl px-5 py-4 flex items-center gap-3 border-b"
      style={{
        background: "rgba(244, 237, 226, 0.82)",
        borderColor: "rgba(27,25,23,0.08)",
      }}
    >
      {onBack !== false && (
        <button
          data-testid="topbar-back-btn"
          onClick={() => (onBack ? onBack() : navigate(-1))}
          className="w-9 h-9 -ml-2 rounded-full grid place-items-center hover:bg-black/5 press"
          aria-label="Back"
          style={{ color: "var(--ink)" }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
      )}
      <div className="flex-1 min-w-0">
        {subtitle && (
          <div className="text-[9px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>
            {subtitle}
          </div>
        )}
        <h1 className="font-display text-[19px] leading-tight truncate mt-0.5" style={{ color: "var(--ink)" }}>{title}</h1>
      </div>
      {right}
    </div>
  );
};

export const BottomNav = () => {
  const { pathname } = useLocation();
  const items = [
    { to: "/", label: "Home", icon: Home, id: "home" },
    { to: "/search", label: "Search", icon: Search, id: "search" },
    { to: "/category", label: "Shop", icon: LayoutGrid, id: "shop" },
    { to: "/admin", label: "Admin", icon: User, id: "admin" },
  ];
  return (
    <nav
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-40 backdrop-blur-xl border-t"
      style={{ background: "rgba(244, 237, 226, 0.88)", borderColor: "rgba(27,25,23,0.08)" }}
    >
      <div className="grid grid-cols-4 px-2 py-2">
        {items.map((it) => {
          const active = pathname === it.to || (it.to !== "/" && pathname.startsWith(it.to));
          const Icon = it.icon;
          return (
            <Link
              key={it.id}
              to={it.to}
              data-testid={`bottomnav-${it.id}`}
              className="flex flex-col items-center py-1.5 gap-1 rounded-xl press"
              style={{ color: active ? "var(--ink)" : "var(--ink-3)" }}
            >
              <Icon size={19} strokeWidth={1.5} />
              <span className="text-[10px] tracking-editorial-sm uppercase">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export const WhatsAppFAB = ({ number, message = "Hello, I'd like to know more about your products." }) => {
  if (!number) return null;
  const clean = (number || "").replace(/[^0-9]/g, "");
  const href = `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
  return (
    <motion.a
      data-testid="whatsapp-fab"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ scale: 0, rotate: -30 }}
      animate={{ scale: 1, rotate: 0 }}
      whileTap={{ scale: 0.94 }}
      className="fixed bottom-24 right-5 z-40 rounded-full bg-[#25D366] text-white w-14 h-14 grid place-items-center shadow-lg shadow-green-500/30"
      aria-label="Chat on WhatsApp"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .18 5.32.18 11.88a11.8 11.8 0 0 0 1.7 6.14L0 24l6.13-1.6a11.87 11.87 0 0 0 5.93 1.51h.01c6.56 0 11.88-5.32 11.88-11.88a11.8 11.8 0 0 0-3.43-8.55zM12.07 21.6h-.01a9.7 9.7 0 0 1-4.94-1.35l-.36-.21-3.63.95.97-3.54-.23-.36a9.71 9.71 0 0 1-1.5-5.21c0-5.36 4.37-9.72 9.72-9.72 2.6 0 5.03 1.01 6.87 2.85a9.65 9.65 0 0 1 2.85 6.87c-.01 5.36-4.37 9.72-9.74 9.72zm5.55-7.28c-.3-.15-1.8-.89-2.08-.99-.28-.1-.48-.15-.68.15s-.78.99-.96 1.19c-.18.2-.35.22-.65.07s-1.28-.47-2.44-1.5c-.9-.8-1.5-1.79-1.68-2.09-.18-.3-.02-.46.13-.6.13-.13.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.68-1.64-.93-2.24-.25-.6-.5-.51-.68-.52h-.58c-.2 0-.53.07-.8.38s-1.05 1.02-1.05 2.48c0 1.46 1.08 2.87 1.23 3.07.15.2 2.12 3.25 5.15 4.55.72.31 1.28.49 1.72.63.72.23 1.38.2 1.9.12.58-.09 1.8-.74 2.05-1.45.25-.71.25-1.32.18-1.45-.08-.13-.27-.2-.57-.35z"/></svg>
    </motion.a>
  );
};
